import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-035914e7"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "panel-outlined h-full align-items-center flex flex-column sm:flex-row" }
const _hoisted_3 = { class: "flex-grow-0 flex-shrink-0 chart-wrapper relative" }
const _hoisted_4 = { class: "chart-center" }
const _hoisted_5 = { class: "title mb-s1" }
const _hoisted_6 = { class: "flex-center" }
const _hoisted_7 = { class: "caption mr-s1" }
const _hoisted_8 = ["id"]
const _hoisted_9 = { class: "legend gutter-y-s2 sm:pl-s10 sm:py-s4" }
const _hoisted_10 = ["onMouseenter", "onMouseleave"]
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "pi pi-circle-fill text-xs" }, null, -1))
const _hoisted_12 = [
  _hoisted_11
]

import { Portfolio, TokenInfo } from '@/sdk/entities/portfolio';
import { computed, ref } from 'vue';
import { chartColors, chartItemColor, documentStyles } from '@/composables/charts/useCharts';
import { format } from '@/utils/format';
import TokenIcon from '@/components/token/TokenIcon.vue';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';
import { useI18n } from 'vue-i18n';
import networks from '@/helpers/networks';
import { getPortfolioTokensWithFirstBaseToken } from '@/helpers/portfolio.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'PortfolioRingChart',
  props: {
    portfolio: null
  },
  setup(__props: any) {

const props = __props as {
  portfolio: Portfolio;
}



const { t } = useI18n();

const chartModel = ref();
const tokenActive = ref();

const availableChains = (token: TokenInfo) => {
  let chains: string[] = [];
  if (props.portfolio.type === PortfolioSource.PORTFOLIO_CROSSCHAIN && !token.isPresentLocally) {
    chains = token.availableOn.map(chainId => networks[chainId].name);
  }
  return chains;
};
const isNotAvailableToken = (token: TokenInfo) => {
  return props.portfolio.type === PortfolioSource.PORTFOLIO_CROSSCHAIN
    ? !token.isPresentLocally
    : false;
};

const chartTokens = computed(() =>
  getPortfolioTokensWithFirstBaseToken(props.portfolio).map((tokenInfo, index) => {
    return {
      symbol: tokenInfo.token.unwrapWETH().symbol,
      img: tokenInfo.token.tokenIconUrl,
      share: tokenInfo.getPortfolioSharePercent(props.portfolio.portfolioTotalValueBase).toNumber(),
      amount: format(
        props.portfolio.getValueInUSD(tokenInfo.amount.toExact(), tokenInfo.getPriceRelative()),
      ),
      color: isNotAvailableToken(tokenInfo)
        ? documentStyles.value.getPropertyValue('--gray-50')
        : chartItemColor(index),
      availableOn: availableChains(tokenInfo),
    };
  }),
);

const chartData = computed(() => {
  return {
    datasets: [
      {
        labels: chartTokens.value.map(item => item.symbol),
        data: chartTokens.value.map(item => item.share),
        backgroundColor: chartColors.value,
        borderColor: documentStyles.value.getPropertyValue('--surface'),
        hoverBackgroundColor: chartColors.value,
        hoverBorderColor: chartColors.value,
        borderWidth: 2,
        hoverOffset: 5,
      },
    ],
  };
});

const getOrCreateTooltip = chart => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add('absolute');
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .3s ease';

    const tooltipContent = document.createElement('div');
    tooltipContent.classList.add('tooltip-text');

    tooltipEl.appendChild(tooltipContent);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};
const externalTooltipHandler = context => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }
  if (tooltip.body) {
    const tooltipContent = tooltipEl.querySelector(`.tooltip-text`);
    while (tooltipContent.firstChild) tooltipContent.firstChild.remove();
    tokenActive.value = chart.getActiveElements().length
      ? chartTokens.value[chart.getActiveElements()[0].index]
      : null;

    const token = document.createElement('div');
    token.classList.add('flex', 'align-items-center', 'gutter-x-s1', 'white-space-nowrap');

    const tokenIcon = document.createElement('img');
    tokenIcon.src = tokenActive.value.img;
    tokenIcon.style.width = '12px';

    const tokenSymbol = document.createElement('span');
    tokenSymbol.append(tokenActive.value.symbol);

    const tokenShare = document.createElement('span');
    tokenShare.style.color = tokenActive.value.color;
    tokenShare.append(`(${format(tokenActive.value.share.toFixed(2))}%)`);

    token.append(tokenIcon, tokenSymbol, tokenShare);

    tooltipContent.append(token);

    if (tokenActive.value.availableOn.length) {
      const tokenInfo0 = document.createElement('div');
      tokenInfo0.classList.add('text-muted', 'text-xs', 'mt-s3', 'white-space-nowrap');
      tokenInfo0.append(t('portfolio.notAvailable'));
      const tokenInfo1 = document.createElement('div');
      tokenInfo1.classList.add('text-muted', 'text-xs', 'mt-s2');
      tokenInfo1.append(t('portfolio.availableOn'));
      const availableOn = tokenActive.value.availableOn;
      availableOn.forEach((chain, index) =>
        tokenInfo1.append(
          document.createElement('br'),
          `${chain}`,
          index !== availableOn.length - 1 ? ',' : '',
        ),
      );
      tooltipContent.append(tokenInfo0, tokenInfo1);
    }
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
};

const chartOptions = computed(() => {
  return {
    onHover: function (e, items) {
      if (items.length) tokenActive.value = chartTokens.value[items[0].index];
    },
    plugins: {
      htmlLegend: {
        containerID: `_l_${props.portfolio.contractAddress} `,
      },
      legend: {
        display: false,
      },
      elements: {
        arc: {
          borderColor: documentStyles.value.getPropertyValue('--surface'),
          borderWidth: 2,
        },
      },
      tooltip: {
        enabled: false,
        position: 'average',
        external: externalTooltipHandler,
      },
    },
    layout: {
      padding: 5,
    },
    cutout: '75%',
    height: '190px',
    width: '190px',
    maintainAspectRatio: false,
    animation: {
      animateRotate: false,
    },
  };
});

const onSetItemActive = (index, isActive) => {
  const chart = chartModel.value.getChart();
  const activeElement = [
    {
      datasetIndex: 0,
      index: index,
    },
  ];
  if (chart.getActiveElements().length > 0) {
    chart.setActiveElements([]);
    chart.tooltip.setActiveElements([]);
  }
  if (isActive) {
    chart.setActiveElements(activeElement);
    chart.tooltip.setActiveElements(activeElement);
  }
  chart.update();
};

return (_ctx: any,_cache: any) => {
  const _component_Chart = _resolveComponent("Chart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, " $" + _toDisplayString(_unref(format)(__props.portfolio.getValueInUSD(__props.portfolio.portfolioTotalValueBase.toExact()))), 1),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(format)(__props.portfolio.portfolioTotalValueBase.toExact())), 1),
            _createVNode(TokenIcon, {
              "icon-src": __props.portfolio.baseToken.tokenIconUrl,
              symbol: __props.portfolio.baseToken.symbol,
              size: "16"
            }, null, 8, ["icon-src", "symbol"])
          ])
        ]),
        _createVNode(_component_Chart, {
          type: "doughnut",
          data: _unref(chartData),
          options: _unref(chartOptions),
          class: "portfolio-chart",
          ref_key: "chartModel",
          ref: chartModel
        }, null, 8, ["data", "options"])
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(`legend-wrapper w-full sm:w-auto`),
        id: `_l_${__props.portfolio.contractAddress}`
      }, [
        _createElementVNode("div", _hoisted_9, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(chartTokens), (token, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: token.symbol
            }, [
              _createElementVNode("div", {
                class: "legend-item text-sm gutter-x-s2 pr-s4 sm:pr-s10",
                onMouseenter: ($event: any) => (onSetItemActive(index, true)),
                onMouseleave: ($event: any) => (onSetItemActive(index, false))
              }, [
                _createElementVNode("div", {
                  class: "legend-dot",
                  style: _normalizeStyle(`--color: ${token.color}`)
                }, _hoisted_12, 4),
                _createElementVNode("div", {
                  class: _normalizeClass(["legend-label", token.availableOn.length ? 'text-muted' : ''])
                }, _toDisplayString(token.symbol), 3),
                _createElementVNode("div", {
                  class: "legend-data",
                  style: _normalizeStyle(`--color: ${token.color}`)
                }, _toDisplayString(_unref(format)(token.share.toFixed(2))) + "% ", 5)
              ], 40, _hoisted_10)
            ]))
          }), 128))
        ])
      ], 8, _hoisted_8)
    ])
  ]))
}
}

})